<template>
  <div class="p-2 sm:p-3">
    <div class="flex justify-center flex-wrap">
      <div class="min-w-full sm:min-w-30 max-w-5xl flex-1 m-2">
        <div class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12">
          <header
              class="flex flex-col md:flex-row items-center justify-between flex-wrap"
          >
            <h1 class="font-semibold text-2xl lg:text-3xl">
              Add Attendees
            </h1>
            <div
                class="w-full md:w-auto flex flex-col md:flex-row items-center justify-end"
            >
              <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 w-full md:w-auto mt-3 md:mt-0"
                  @click="onClickRunDemo"
              >
                Run Demo
              </button>
              <input
                  id="attendees_file"
                  accept=".csv"
                  hidden
                  name="attendees_file"
                  type="file"
                  @change="onSelectFileToUpload"
              />
              <label
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 w-full md:w-auto md:ml-4 mt-3 md:mt-0 text-center"
                  for="attendees_file"
              >
                Upload CSV
              </label>
              <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 w-full md:w-auto md:ml-4 mt-3 md:mt-0"
                  @click="onClickAddAttendee"
              >
                + Add Attendee
              </button>
            </div>
          </header>
          <section class="mt-12">
            <div v-if="progressLoading">
              <div class="text-center text-gray-600 my-2 p-5">
                <Spinner class="mx-auto" orange/>
              </div>
            </div>
            <div v-else-if="attendees.length === 0">
              <div class="text-center text-gray-600 font-semibold my-2 p-5">
                <div>You can add attendees manually</div>
                <div>Or import a .csv file</div>
              </div>
            </div>
            <div v-for="attendee in attendees" v-else :key="attendee.index">
              <div class="md:flex mt-12 md:mt-5">
                <div class="flex">
                  <label>
                    <span class="text-sm">First Name</span>
                    <input
                        v-model="attendee.first_name"
                        class="input mt-2"
                        type="text"
                    />
                  </label>
                  <label class="ml-5">
                    <span class="text-sm">Last Name</span>
                    <input
                        v-model="attendee.last_name"
                        class="input mt-2"
                        type="text"
                    />
                  </label>
                </div>
                <div class="flex items-end md:ml-5 mt-4 md:mt-0">
                  <label class="flex-1">
                    <span class="text-sm">Email</span>
                    <input
                        v-model="attendee.email"
                        class="input mt-2"
                        type="text"
                    />
                  </label>
                  <button
                      class="btn p-3 ml-2 flex-shrink-0"
                      @click="onClickRemoveAttendee(attendee)"
                  >
                    <img
                        alt="BuildaBox Order Process"
                        src="@/assets/icons/ic_remove.svg"
                    />
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section class="mt-16">
            <div class="flex flex-col-reverse sm:flex-row justify-between">
              <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mt-5 sm:mt-0"
                  @click="close"
              >
                <span>Close</span>
              </button>
              <div>
                <button
                    :disabled="!canSubmit"
                    class="btn btn-primary font-semibold rounded-lg px-10 min-w-10"
                    @click="submit"
                >
                  <span v-if="!progressSaving">Invite</span>
                  <Spinner v-if="progressSaving" class="mx-auto"/>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="m-2">
        <div
            class="bg-white rounded-lg shadow-card p-8 self-start max-w-sm sm:p-12 mb-4"
        >
          <div class="flex flex-col h-full">
            <img
                alt="BuildaBox Order Process"
                class="mb-4 "
                height="32"
                src="@/assets/icons/info.svg"
                width="32"
            />
            <div class="mb-3">
              <h1 class="font-semibold text-lg">Add Attendees By CSV</h1>
            </div>
            <p>
              Attendees can be added in bulk using a CSV file. Select the button
              below to download a sample and upload it to this page.
            </p>
            <div class="text-center mt-12">
              <a
                  class="btn btn-primary font-semibold inline-block px-10 rounded-lg"
                  href="/files/attendeesSample.csv"
              >
                Download Sample
              </a>
            </div>
          </div>
        </div>

        <InfoBox :showIcon="true" title="Add Attendees by Public Form">
          <p>
            Add attendees automatically by providing them with the link to the
            form below. Anyone that completes the form will be added to the
            attendee list. You can manually remove incorrect attendees from this
            page or through order management.
          </p>
          <div class="mt-4">
            <a
                :href="getInviteLink()"
                class="btn font-semibold rounded-lg break-all"
                target="_blank"
            >
              {{ getInviteLink() }}
            </a>
          </div>
        </InfoBox>

      </div>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/common/Spinner'
import Api from '@/api/api_v1.0'
import { email, minLength, required } from 'vuelidate/lib/validators'

import InfoBox from '@/components/common/InfoBox'
import { EventBus } from '@/utils/event-bus'

export default {
  components: { Spinner, InfoBox },
  data () {
    return {
      attendees      : [],
      progressLoading: false,
    }
  },
  computed   : {
    order () {
      return this.$store.state.currentEditingOrder
    },
    canSubmit () {
      if (this.attendees.length === 0) {
        return true
      } else {
        return !this.$v.$invalid
      }
    },
    progressSaving: {
      get () {
        return this.$store.state.isSavingCurrentOrder
      },
      set (value) {
        this.$store.commit('setIsSavingCurrentOrder', value)
      }
    }
  },
  validations: {
    attendees: {
      $each: {
        first_name: { required, minLength: minLength(1) },
        last_name : { required, minLength: minLength(1) },
        email     : { required, email }
      }
    }
  },
  created () {
    EventBus.$on('saveCurrentOrderStep', this.submit)
  },
  async mounted () {
    await this.loadAttendees()
  },
  beforeDestroy () {
    EventBus.$off('saveCurrentOrderStep', this.submit)
  },
  methods: {
    close () {
      this.$emit('close', true)
    },
    loadAttendees () {
      this.progressLoading = true

      Api.getAttendeesForOrder(this.order.uuid)
         .then(response => {
           this.progressLoading = false
           this.attendees = response.data.data.map((attendee, index) => {
             attendee.index = index + 1
             return attendee
           })
           this.paginationMeta = response.data.meta
         })
         .catch(() => {
           this.progressLoading = false
         })
    },
    onClickRunDemo () {
      this.progressLoading = true
      Api.runOrderDemo(this.order.uuid)
         .then(() => {
           this.progressLoading = false
           this.$store.dispatch(
               'setSuccessMessage',
               'We have sent you an attendee invite link.'
           )

           this.$gtm.trackEvent({
             event   : 'run_demo',
             category: 'Order',
             action  : 'runDemo',
             label   : 'Run Demo activated',
             value   : this.order.uuid
           })

           this.loadAttendees()
         })
         .catch(() => {
           this.progressLoading = false
         })
    },
    onSelectFileToUpload (event) {
      const file = event.target.files[0]
      this.progressLoading = true
      Api.uploadAttendeesToOrder(this.order.uuid, file)
         .then(() => {
           this.progressLoading = false
           this.loadAttendees()
         })
         .catch(() => {
           this.progressLoading = false
         })
    },
    onClickAddAttendee () {
      const attendee = {
        index     : this.attendees.length + 1,
        id        : '',
        first_name: '',
        last_name : '',
        email     : ''
      }
      this.attendees.unshift(attendee)
    },
    onClickRemoveAttendee (attendee) {
      const index = this.attendees.findIndex(
          item => item.index === attendee.index
      )
      this.attendees.splice(index, 1)
    },
    getInviteLink () {
      const path = this.$router.resolve({
        name  : 'orders.invite',
        params: { uuid: this.order.uuid }
      }).href
      return `${window.origin}${path}`
    },
    submit () {
      this.progressSaving = true
      Api.addMultipleAttendeesToOrder(this.order.uuid, this.attendees)
         .then(() => {
           this.progressSaving = false
           this.$store.dispatch(
               'setSuccessMessage',
               'Attendees invite sent.'
           )
           this.$emit('submit')
         })
         .catch(() => {
           this.progressSaving = false
         })
    }
  }
}
</script>
